import './MetricsSelector.scss';
import React, { useState, useEffect, useMemo } from 'react';
import { FlightSelect } from '@flybits/design-system';
import { EMAIL_METRIC_OPTIONS, METRIC_OPTIONS, SMS_METRIC_OPTIONS } from '../constants';
import { ChartConfigState, MetricOption } from 'pages/Analytics/types';

const MAIN_CLASS = 'metrics-selector';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  DD: `${MAIN_CLASS}__select`,
};

type TMetricsSelectorProps = {
  chartConfig: ChartConfigState;
  isEmailSmsOptionEnabled?: boolean;
  onChange: (metric?: MetricOption['value']) => void;
  onValidation: (isValid: boolean) => void;
};

export default function MetricsSelector({
  chartConfig,
  isEmailSmsOptionEnabled,
  onChange,
  onValidation,
}: TMetricsSelectorProps) {
  const { metrics } = chartConfig;

  // TODO: This logic currently separates existing metrics from Email/SMS metrics manually (introduced in PET-5699).
  // The actual list of DIY metrics options should be derived dynamically from the selected experience and its available triggers and actions.
  // A spike ticket (PET-5857) exists to analyze and refactor this to remove hardcoded logic.
  const filterMetricOptions = useMemo(() => {
    if (!isEmailSmsOptionEnabled) {
      return METRIC_OPTIONS;
    }
    const experience = chartConfig.selectedExperiences?.[0];

    if (experience?.hasEmail && experience?.hasSMS) {
      return [...EMAIL_METRIC_OPTIONS, ...SMS_METRIC_OPTIONS];
    }
    if (experience?.hasEmail) {
      return EMAIL_METRIC_OPTIONS;
    }
    if (experience?.hasSMS) {
      return SMS_METRIC_OPTIONS;
    }
    return METRIC_OPTIONS;
  }, [chartConfig.selectedExperiences, isEmailSmsOptionEnabled]);

  const initialOption = useMemo(() => {
    let initOption: MetricOption = {
      key: 'none',
      name: 'None Selected',
      value: {
        title: '',
        entity: '',
        metric: '',
      },
    };

    if (metrics) {
      initOption =
        filterMetricOptions.find((o) => o.value?.entity === metrics.entity && o.value?.metric === metrics.metric) ||
        initOption;
    }
    return initOption;
  }, [filterMetricOptions, metrics]);

  const [selectedOption, setSelectedOption] = useState<MetricOption>();

  useEffect(() => {
    const isValid = !!selectedOption?.value || (!!initialOption.value?.entity && !!initialOption.value?.metric);
    onValidation(isValid);
  }, [selectedOption, initialOption, onValidation]);

  return (
    <section className={MAIN_CLASS}>
      <label className={CLASSES.LABEL} htmlFor="select-interval">
        Select a metric
      </label>
      <FlightSelect
        className={`${CLASSES.DD}`}
        options={filterMetricOptions}
        selected={selectedOption || initialOption}
        width={'100%'}
        handleOptionClick={(option: (typeof METRIC_OPTIONS)[number]) => {
          setSelectedOption(option);
          onChange && onChange(option.value);
        }}
      />
    </section>
  );
}
